import { useMemo, useState } from 'react';
import { useAppData } from '../app/AppProvider';
import { EmptyState } from '../components/EmptyState';
import { FiltersDialog } from '../components/FiltersDialog';
import { Icon } from '../components/Icon';
import { LineDots } from '../components/LineDots';
import { PlaceCard } from '../components/PlaceCard';
import { StationCard } from '../components/StationCard';
import { METRO_STATION_BY_ID, METRO_STATIONS } from '../data/metro';
import { INITIAL_TAGS } from '../domain/labels';
import { collectPlaceTags, hasActivePlaceFilters, searchAndFilterPlaces } from '../domain/search';
import type { PlaceFilters } from '../domain/types';

const collator = new Intl.Collator('ru', { sensitivity: 'base' });

export function HomePage() {
  const { data, setStationFavorite } = useAppData();
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<PlaceFilters>({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  const availableTags = useMemo(() => {
    const values = [...INITIAL_TAGS, ...data.customTags, ...collectPlaceTags(data.places)];
    return [...new Map(values.map((tag) => [tag.toLocaleLowerCase('ru'), tag])).values()]
      .sort((left, right) => collator.compare(left, right));
  }, [data.customTags, data.places]);

  const activeFilters = hasActivePlaceFilters(filters);
  const filtering = query.trim().length > 0 || activeFilters;
  const filterCount = (filters.statuses?.length ?? 0) + (filters.returnIntents?.length ?? 0) + (filters.tags?.length ?? 0);

  const filteredPlaces = useMemo(
    () => searchAndFilterPlaces(data.places, query, filters, METRO_STATIONS),
    [data.places, filters, query],
  );

  const usedStationIds = useMemo(() => {
    const ids = new Set(data.favoriteStationIds);
    data.places.forEach((place) => ids.add(place.stationId));
    return ids;
  }, [data.favoriteStationIds, data.places]);

  const favoriteStations = METRO_STATIONS
    .filter((station) => data.favoriteStationIds.includes(station.id))
    .sort((left, right) => collator.compare(left.name, right.name));

  const otherStations = METRO_STATIONS
    .filter((station) => usedStationIds.has(station.id) && !data.favoriteStationIds.includes(station.id))
    .sort((left, right) => collator.compare(left.name, right.name));

  const resultGroups = useMemo(() => {
    const groups = new Map<string, typeof filteredPlaces>();
    for (const place of filteredPlaces) {
      const group = groups.get(place.stationId) ?? [];
      group.push(place);
      groups.set(place.stationId, group);
    }
    return [...groups.entries()]
      .map(([stationId, places]) => ({
        station: METRO_STATION_BY_ID.get(stationId),
        places: [...places].sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
      }))
      .filter((group): group is { station: NonNullable<typeof group.station>; places: typeof group.places } => Boolean(group.station))
      .sort((left, right) => collator.compare(left.station.name, right.station.name));
  }, [filteredPlaces]);

  const placesForStation = (stationId: string) => data.places.filter((place) => place.stationId === stationId);

  return (
    <main className="page">
      <div className="home-title-row">
        <h1 className="home-title">Места</h1>
        <a className="icon-button" href="#/settings" aria-label="Настройки"><Icon name="gear" /></a>
      </div>

      <div className="search-row">
        <div className="search-box">
          <Icon name="search" />
          <input
            className="search-input"
            type="search"
            value={query}
            placeholder="Место, метро, тег…"
            aria-label="Поиск по местам"
            onChange={(event) => setQuery(event.target.value)}
          />
          {query ? <button className="clear-search" type="button" aria-label="Очистить поиск" onClick={() => setQuery('')}><Icon name="close" /></button> : null}
        </div>
        <button
          className={`filter-button ${activeFilters ? 'filter-button--active' : ''}`}
          type="button"
          aria-label="Открыть фильтры"
          onClick={() => setFiltersOpen(true)}
        >
          <Icon name="filter" />
          {filterCount > 0 ? <span className="filter-count">{filterCount}</span> : null}
        </button>
      </div>

      {filtering ? (
        <section className="section">
          <div className="section-heading-row">
            <h2 className="section-heading">Найденные места</h2>
            <button className="button button--quiet" type="button" onClick={() => { setQuery(''); setFilters({}); }}>Сбросить всё</button>
          </div>
          {resultGroups.length > 0 ? (
            <div className="result-groups">
              {resultGroups.map(({ station, places }) => (
                <section className="result-group" key={station.id}>
                  <h3 className="result-group__title"><LineDots lines={station.lines} /> {station.name}</h3>
                  <div className="card-list">{places.map((place) => <PlaceCard key={place.id} place={place} />)}</div>
                </section>
              ))}
            </div>
          ) : (
            <EmptyState title="Ничего не найдено">Попробуйте изменить запрос или сбросить часть фильтров.</EmptyState>
          )}
        </section>
      ) : (
        <>
          <section className="section">
            <div className="section-heading-row">
              <h2 className="section-heading">Избранные станции</h2>
              <span className="section-count">{favoriteStations.length}</span>
            </div>
            {favoriteStations.length > 0 ? (
              <div className="station-list">
                {favoriteStations.map((station) => (
                  <StationCard key={station.id} station={station} places={placesForStation(station.id)} favorite onFavoriteChange={(favorite) => void setStationFavorite(station.id, favorite)} />
                ))}
              </div>
            ) : <p className="muted">Отмеченные звёздочкой станции появятся здесь.</p>}
          </section>

          <section className="section">
            <div className="section-heading-row">
              <h2 className="section-heading">Остальные станции</h2>
              <span className="section-count">{otherStations.length}</span>
            </div>
            {otherStations.length > 0 ? (
              <div className="station-list">
                {otherStations.map((station) => (
                  <StationCard key={station.id} station={station} places={placesForStation(station.id)} favorite={false} onFavoriteChange={(favorite) => void setStationFavorite(station.id, favorite)} />
                ))}
              </div>
            ) : favoriteStations.length === 0 ? (
              <EmptyState title="Пока нет мест">Добавьте первое место — его станция появится на главном экране.</EmptyState>
            ) : <p className="muted">Все используемые станции уже в избранном.</p>}
          </section>
        </>
      )}

      <a className="fab" href="#/place/new"><Icon name="plus" /> Добавить место</a>
      <FiltersDialog open={filtersOpen} filters={filters} availableTags={availableTags} onApply={setFilters} onClose={() => setFiltersOpen(false)} />
    </main>
  );
}
